import {
  Box,
  Button,
  Text,
  Container,
  VStack,
  HStack,
  Icon,
  Heading,
  useColorModeValue,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Badge,
  Spinner,
  useToast,
  useDisclosure,
  SimpleGrid,
  useBreakpointValue,
  Divider,
  Image,
  Tooltip,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Select,
} from "@chakra-ui/react";
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaArrowLeft, FaRoute, FaIndustry, FaBuilding, FaMapMarkerAlt, FaClock, FaCalendarAlt, FaTruck, FaUser, FaIdCard, FaUsers, FaChartBar, FaDirections, FaUtensils, FaPause, FaHourglassHalf } from "react-icons/fa";
import { MdBusiness, MdSchedule, MdLocationOn, MdDirectionsCar, MdRestaurant, MdPause, MdHourglassEmpty, MdLocalShipping } from "react-icons/md";
import { fetchEventosAtuais } from "../apis/get-eventos-atuais";
import SSLCertificateHelper from "../components/SSLCertificateHelper";
import LicenseErrorAlert from "../components/LicenseErrorAlert";

function EventosAtuais() {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const sslHelperDisclosure = useDisclosure();
  
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sslError, setSslError] = useState(null);
  const [filteredEventos, setFilteredEventos] = useState([]);
  const [statusFilter, setStatusFilter] = useState("todos");
  const [eventoFilter, setEventoFilter] = useState("todos");
  
  // Estados para controle de datas
  const [dataInicial, setDataInicial] = useState("");
  const [dataFinal, setDataFinal] = useState("");
  const [periodoSelecionado, setPeriodoSelecionado] = useState("3dias");
  
  // Estado para armazenar endereços dos eventos
  const [enderecos, setEnderecos] = useState({});
  const [carregandoEnderecos, setCarregandoEnderecos] = useState(false);
  
  // Estado para forçar re-render dos eventos ativos (atualização em tempo real)
  const [tempoAtual, setTempoAtual] = useState(new Date());
  
  // Cores responsivas
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.800", "white");
  const mutedTextColor = useColorModeValue("gray.600", "gray.400");

  // Breakpoints responsivos
  const gridCols = useBreakpointValue({ base: 1, md: 2, lg: 3 });

  // Verificar se o motorista foi passado via state
  const motorista = location.state?.motorista;
  const periodoInicial = location.state?.periodoInicial || "3dias";
  
  // Função para calcular datas baseadas no período selecionado
  const calcularDatas = (periodo) => {
    const hoje = new Date();
    
    // Função mais robusta para formatar data
    const formatarData = (data) => {
      const ano = data.getFullYear();
      const mes = String(data.getMonth() + 1).padStart(2, '0');
      const dia = String(data.getDate()).padStart(2, '0');
      return `${ano}-${mes}-${dia}`;
    };
    
    console.log("=== DEBUG CALCULAR DATAS ===");
    console.log("Data atual (hoje):", hoje);
    console.log("Data atual formatada:", formatarData(hoje));
    console.log("Timezone offset:", hoje.getTimezoneOffset());
    console.log("Data UTC:", hoje.toISOString());
    console.log("Data local:", hoje.toLocaleDateString('pt-BR'));
    console.log("Data ISO:", hoje.toISOString().split('T')[0]);
    
    switch (periodo) {
      case "hoje":
        const hojeResult = {
          inicial: formatarData(hoje),
          final: formatarData(hoje)
        };
        console.log("Período HOJE:", hojeResult);
        return hojeResult;
      case "ontem":
        const ontem = new Date(hoje);
        ontem.setDate(hoje.getDate() - 1);
        const ontemResult = {
          inicial: formatarData(ontem),
          final: formatarData(ontem)
        };
        console.log("Período ONTEM:", ontemResult);
        return ontemResult;
      case "semana":
        const inicioSemana = new Date(hoje);
        inicioSemana.setDate(hoje.getDate() - 3);
        const fimSemana = new Date(hoje);
        fimSemana.setDate(hoje.getDate() + 3);
        const semanaResult = {
          inicial: formatarData(inicioSemana),
          final: formatarData(fimSemana)
        };
        console.log("Período SEMANA:", semanaResult);
        return semanaResult;
      case "mes":
        const inicioMes = new Date(hoje);
        inicioMes.setDate(hoje.getDate() - 15);
        const fimMes = new Date(hoje);
        fimMes.setDate(hoje.getDate() + 15);
        const mesResult = {
          inicial: formatarData(inicioMes),
          final: formatarData(fimMes)
        };
        console.log("Período MÊS:", mesResult);
        return mesResult;
      case "3dias":
      default:
        const ontem3 = new Date(hoje);
        ontem3.setDate(hoje.getDate() - 1);
        const amanha3 = new Date(hoje);
        amanha3.setDate(hoje.getDate() + 1);
        const tresDiasResult = {
          inicial: formatarData(ontem3),
          final: formatarData(amanha3)
        };
        console.log("Período 3 DIAS:", tresDiasResult);
        return tresDiasResult;
    }
  };
  
  // Função para alterar período
  const alterarPeriodo = async (novoPeriodo) => {
    setPeriodoSelecionado(novoPeriodo);
    const datas = calcularDatas(novoPeriodo);
    setDataInicial(datas.inicial);
    setDataFinal(datas.final);
    
    // Limpar endereços anteriores
    setEnderecos({});
    
    // Recarregar eventos com novo período
    await loadEventos(datas.inicial, datas.final);
    
    // Aplicar filtros será feito pelo useEffect quando eventos for atualizado
  };

  useEffect(() => {
    if (!motorista) {
      toast({
        title: "Motorista não encontrado",
        description: "Por favor, selecione um motorista primeiro",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      navigate("/", { replace: true });
      return;
    }

    // Inicializar datas com período recebido ou padrão (3 dias)
    const datas = calcularDatas(periodoInicial);
    setDataInicial(datas.inicial);
    setDataFinal(datas.final);
    setPeriodoSelecionado(periodoInicial);
    loadEventos(datas.inicial, datas.final);
  }, [motorista, navigate, toast]);

  // Atualizar tempo a cada minuto para eventos ativos
  useEffect(() => {
    const interval = setInterval(() => {
      setTempoAtual(new Date());
    }, 60000); // 60 segundos = 1 minuto

    return () => clearInterval(interval);
  }, []);

  const loadEventos = async (dataIni, dataFim) => {
    try {
      setLoading(true);
      console.log("=== DEBUG LOAD EVENTOS ===");
      console.log("Motorista:", motorista.codigo);
      console.log("Data inicial solicitada:", dataIni);
      console.log("Data final solicitada:", dataFim);
      
      const data = await fetchEventosAtuais(motorista.codigo, dataIni, dataFim);
      
      console.log("Dados brutos recebidos da API:", data);
      console.log("Quantidade de eventos recebidos:", data.length);
      
      // Filtrar eventos apenas do motorista selecionado
      const eventosMotorista = data.filter(evento => 
        evento.id_motorista === motorista.codigo
      );
      
      console.log("Eventos filtrados por motorista:", eventosMotorista.length);
      console.log("Primeiros 3 eventos:", eventosMotorista.slice(0, 3).map(e => ({
        id: e.id,
        data_inicial: e.data_inicial,
        data_final: e.data_final,
        hora_inicial: e.hora_inicial,
        hora_final: e.hora_final,
        descricao_evento: e.descricao_evento
      })));
      
      console.log("=== DEBUG SET EVENTOS ===");
      console.log("Eventos antes de setEventos:", eventos.length);
      console.log("EventosMotorista para setar:", eventosMotorista.length);
      
      setEventos(eventosMotorista);
      setError(null);
      setSslError(null);
      
      console.log("=== DEBUG APÓS SET EVENTOS ===");
      console.log("Estado eventos após setEventos:", eventos.length);
      
      // Carregar endereços dos eventos
      await carregarEnderecos(eventosMotorista);
      
      // Aplicar filtros será feito pelo useEffect quando eventos for atualizado
    } catch (err) {
      console.error("Erro ao carregar eventos:", err);
      
      if (err.type === 'SSL_ERROR') {
        setSslError(err);
        sslHelperDisclosure.onOpen();
      } else {
        setError(err.message);
        toast({
          title: "Erro ao carregar eventos",
          description: err.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const getEmpresaConfig = (empresa) => {
    switch (empresa) {
      case "IMPACTO":
        return {
          color: "blue",
          borderColor: "blue.500",
          icon: FaIndustry,
          bgColor: "blue.50",
          logo: "/impacto_transportes_ltda_logo.jpg",
          gradient: "linear-gradient(135deg, #EBF8FF 0%, #BEE3F8 100%)"
        };
      case "CENZE":
        return {
          color: "purple",
          borderColor: "purple.500",
          icon: MdBusiness,
          bgColor: "purple.50",
          logo: "/trr_cenze_comrcio_de_combustveis_e_derivados_logo.jpg",
          gradient: "linear-gradient(135deg, #FAF5FF 0%, #E9D8FD 100%)"
        };
      case "SEM EMPRESA":
      default:
        return {
          color: "gray",
          borderColor: "gray.500",
          icon: FaBuilding,
          bgColor: "gray.50",
          logo: null,
          gradient: "linear-gradient(135deg, #F7FAFC 0%, #EDF2F7 100%)"
        };
    }
  };

  const getEventoConfig = (descricaoEvento) => {
    switch (descricaoEvento) {
      case "DIRECAO":
        return {
          color: "green",
          icon: MdDirectionsCar,
          label: "Direção",
          bgColor: "green.50",
          borderColor: "green.500"
        };
      case "REFEICAO":
        return {
          color: "orange",
          icon: MdRestaurant,
          label: "Refeição",
          bgColor: "orange.50",
          borderColor: "orange.500"
        };
      case "INTERSTICIO":
        return {
          color: "blue",
          icon: MdPause,
          label: "Interstício",
          bgColor: "blue.50",
          borderColor: "blue.500"
        };
      case "TEMPO ESPERA":
        return {
          color: "yellow",
          icon: MdHourglassEmpty,
          label: "Tempo de Espera",
          bgColor: "yellow.50",
          borderColor: "yellow.500"
        };
      default:
        return {
          color: "gray",
          icon: MdSchedule,
          label: descricaoEvento,
          bgColor: "gray.50",
          borderColor: "gray.500"
        };
    }
  };

  const getStatusConfig = (status) => {
    if (!status) return { color: "gray", label: "Sem Status" };
    
    switch (status.toUpperCase()) {
      case "CARGA":
        return { color: "blue", label: "Carga", icon: MdLocalShipping };
      case "DESCARGA":
        return { color: "green", label: "Descarga", icon: MdLocalShipping };
      default:
        return { color: "gray", label: status, icon: MdSchedule };
    }
  };

  const formatDateTime = (data, hora) => {
    if (!data || !hora) return "N/A";
    try {
      // Usar UTC para evitar problemas de timezone
      const [ano, mes, dia] = data.split('-');
      const [horaH, min] = hora.split(':');
      const date = new Date(Date.UTC(parseInt(ano), parseInt(mes) - 1, parseInt(dia), parseInt(horaH), parseInt(min)));
      
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return `${data} ${hora}`;
    }
  };

  const calculateDuration = (dataInicial, horaInicial, dataFinal, horaFinal, descricaoEvento) => {
    if (!dataInicial || !horaInicial || !dataFinal || !horaFinal) return "N/A";
    
    try {
      // Usar UTC para evitar problemas de timezone
      const [anoIni, mesIni, diaIni] = dataInicial.split('-');
      const [anoFim, mesFim, diaFim] = dataFinal.split('-');
      const [horaIniH, minIni] = horaInicial.split(':');
      const [horaFimH, minFim] = horaFinal.split(':');
      
      const start = new Date(Date.UTC(parseInt(anoIni), parseInt(mesIni) - 1, parseInt(diaIni), parseInt(horaIniH), parseInt(minIni)));
      const end = new Date(Date.UTC(parseInt(anoFim), parseInt(mesFim) - 1, parseInt(diaFim), parseInt(horaFimH), parseInt(minFim)));
      
      // Verificar se é um evento em andamento
      const isEventoAtivo = isEventoEmAndamento(dataInicial, horaInicial, dataFinal, horaFinal, descricaoEvento);
      
      let endTime = end;
      if (isEventoAtivo) {
        // Se for evento ativo, usar o tempo atual do estado
        endTime = tempoAtual;
      }
      
      const diffMs = endTime - start;
      
      if (diffMs < 0) return "0h 0m";
      
      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      
      const duration = `${hours}h ${minutes}m`;
      
      // Para eventos ativos, mostrar apenas a duração (sem indicador)
      return duration;
    } catch {
      return "N/A";
    }
  };

  // Função para verificar se um evento está em andamento
  const isEventoEmAndamento = (dataInicial, horaInicial, dataFinal, horaFinal, descricaoEvento) => {
    // Eventos que não podem estar em andamento
    const eventosNaoAtivos = ['INTERSTICIO', 'DESCANSO SEMANAL', 'FERIAS'];
    
    if (eventosNaoAtivos.includes(descricaoEvento)) {
      return false;
    }
    
    // Verificar se termina com 23:59 (indicador de evento em andamento)
    if (horaFinal === "23:59") {
      return true;
    }
    
    // Verificar se o evento está acontecendo agora
    try {
      const [anoIni, mesIni, diaIni] = dataInicial.split('-');
      const [horaIniH, minIni] = horaInicial.split(':');
      
      const startTime = new Date(Date.UTC(parseInt(anoIni), parseInt(mesIni) - 1, parseInt(diaIni), parseInt(horaIniH), parseInt(minIni)));
      
      // Se o evento começou e ainda não terminou (considerando 23:59 como "ainda em andamento")
      return startTime <= tempoAtual && horaFinal === "23:59";
    } catch (error) {
      console.error("Erro em isEventoEmAndamento:", error);
      return false;
    }
  };

  // Função para obter endereço baseado nas coordenadas GPS
  const getEndereco = async (latitude, longitude) => {
    if (!latitude || !longitude) return "Localização não disponível";
    
    try {
      // Usar a API gratuita do OpenStreetMap (Nominatim)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1&accept-language=pt-BR`,
        {
          headers: {
            'User-Agent': 'PapeletaDigital/1.0' // Identificação do app para a API
          }
        }
      );
      
      if (!response.ok) {
        throw new Error(`Erro na API: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      // Extrair informações relevantes do endereço
      const address = data.address;
      let endereco = "";
      
      // Construir endereço de forma hierárquica
      if (address.road) {
        endereco += address.road;
        if (address.house_number) endereco += `, ${address.house_number}`;
        if (address.suburb) endereco += `, ${address.suburb}`;
      } else if (address.suburb) {
        endereco += address.suburb;
      }
      
      if (address.city || address.town || address.village) {
        if (endereco) endereco += " - ";
        endereco += address.city || address.town || address.village;
      }
      
      if (address.state) {
        if (endereco) endereco += ", ";
        endereco += address.state;
      }
      
      // Se não conseguiu construir um endereço legível, usar o display_name
      if (!endereco && data.display_name) {
        endereco = data.display_name.split(', ').slice(0, 3).join(', ');
      }
      
      return endereco || "Endereço não disponível";
      
    } catch (error) {
      console.error("Erro ao obter endereço:", error);
      // Em caso de erro, retornar coordenadas formatadas
      return `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
    }
  };

  // Função para carregar endereços de todos os eventos
  const carregarEnderecos = async (eventosList) => {
    setCarregandoEnderecos(true);
    const novosEnderecos = {};
    
    try {
      for (const evento of eventosList) {
        if (evento.latitude && evento.longitude) {
          try {
            const endereco = await getEndereco(evento.latitude, evento.longitude);
            novosEnderecos[evento.id] = endereco;
          } catch (error) {
            console.error(`Erro ao carregar endereço para evento ${evento.id}:`, error);
            novosEnderecos[evento.id] = `${evento.latitude.toFixed(4)}, ${evento.longitude.toFixed(4)}`;
          }
        }
      }
      
      setEnderecos(novosEnderecos);
    } finally {
      setCarregandoEnderecos(false);
    }
  };

  const applyFilters = () => {
    console.log("=== DEBUG APPLY FILTERS ===");
    console.log("Eventos originais:", eventos.length);
    console.log("Status filter:", statusFilter);
    console.log("Evento filter:", eventoFilter);
    
    let filtered = eventos;

    // Filtro por status
    if (statusFilter !== "todos") {
      filtered = filtered.filter(evento => {
        if (statusFilter === "comStatus") {
          return evento.status && evento.status.trim() !== "";
        } else if (statusFilter === "semStatus") {
          return !evento.status || evento.status.trim() === "";
        }
        return true;
      });
    }

    // Filtro por tipo de evento
    if (eventoFilter !== "todos") {
      filtered = filtered.filter(evento => evento.descricao_evento === eventoFilter);
    }

    console.log("Eventos filtrados:", filtered.length);
    console.log("Primeiros eventos filtrados:", filtered.slice(0, 3));
    
    setFilteredEventos(filtered);
  };

  useEffect(() => {
    // Só aplicar filtros quando houver eventos carregados
    if (eventos.length > 0) {
      console.log("=== DEBUG USE EFFECT FILTERS ===");
      console.log("Eventos no useEffect:", eventos.length);
      console.log("Chamando applyFilters...");
      applyFilters();
    }
  }, [eventos, statusFilter, eventoFilter]);

  const clearFilters = () => {
    setStatusFilter("todos");
    setEventoFilter("todos");
    
    // Aplicar filtros será feito pelo useEffect quando eventos for atualizado
  };

  const handleVoltar = () => {
    navigate(`/${motorista.codigo}/escolher-acao`, {
      state: { motorista, periodoInicial },
    });
  };

  const handleVoltarSelecao = () => {
    navigate("/", { replace: true });
  };

  // Estatísticas
  const getStats = () => {
    const total = eventos.length;
    const direcao = eventos.filter(e => e.descricao_evento === "DIRECAO").length;
    const refeicao = eventos.filter(e => e.descricao_evento === "REFEICAO").length;
    const intersticio = eventos.filter(e => e.descricao_evento === "INTERSTICIO").length;
    const tempoEspera = eventos.filter(e => e.descricao_evento === "TEMPO ESPERA").length;
    const comStatus = eventos.filter(e => e.status && e.status.trim() !== "").length;
    
    // Contar eventos ativos
    const ativos = eventos.filter(e => 
      isEventoEmAndamento(e.data_inicial, e.hora_inicial, e.data_final, e.hora_final, e.descricao_evento)
    ).length;
    
    return { total, direcao, refeicao, intersticio, tempoEspera, comStatus, ativos };
  };

  const stats = getStats();

  if (!motorista) {
    return null;
  }

  if (loading) {
    return (
      <Flex 
        justify="center" 
        align="center" 
        height="100vh" 
        bg={bgColor}
        direction="column"
        gap={6}
      >
        <Box
          bg={cardBg}
          p={8}
          borderRadius="2xl"
          boxShadow="xl"
          border="1px solid"
          borderColor={borderColor}
          textAlign="center"
        >
          <Spinner size="xl" color="blue.500" thickness="4px" mb={4} />
          <Text fontSize="lg" color={textColor} fontWeight="medium">
            Carregando eventos...
          </Text>
          <Text fontSize="sm" color={mutedTextColor} mt={2}>
            Aguarde enquanto buscamos as informações
          </Text>
        </Box>
      </Flex>
    );
  }

  if (error) {
    // Verificar se é erro de licença
    const isLicenseError = error.includes("licenca");
    
    if (isLicenseError) {
      return (
        <Box
          minH="100vh"
          bg={bgColor}
          display="flex"
          alignItems="center"
          justifyContent="center"
          p={6}
        >
          <LicenseErrorAlert
            error={{ message: error }}
            onRetry={() => loadEventos(dataInicial, dataFinal)}
            onBack={handleVoltar}
            context="carregar eventos atuais"
          />
        </Box>
      );
    }
    
    // Erro genérico
    return (
      <Box
        minH="100vh"
        bg={bgColor}
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={6}
      >
        <Box
          maxW="500px"
          w="100%"
          bg={cardBg}
          borderRadius="2xl"
          boxShadow="2xl"
          border="1px solid"
          borderColor="red.100"
          overflow="hidden"
        >
          <Box
            bg="red.500"
            p={6}
            textAlign="center"
          >
            <Text fontSize="2xl" fontWeight="bold" color="white" mb={2}>
              Erro ao Carregar Eventos
            </Text>
            <Text fontSize="md" color="red.100" opacity={0.9}>
              Não foi possível obter os eventos do motorista
            </Text>
          </Box>

          <Box p={8}>
            <Text fontSize="lg" color={textColor} textAlign="center" mb={6} lineHeight="tall">
              {error}
            </Text>

            <VStack spacing={4} mt={8}>
              <Button
                colorScheme="red"
                size="lg"
                w="100%"
                onClick={() => loadEventos(dataInicial, dataFinal)}
                borderRadius="xl"
                fontWeight="bold"
                fontSize="md"
                py={6}
                _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
                transition="all 0.2s"
              >
                Tentar Novamente
              </Button>
              
              <Button
                colorScheme="gray"
                variant="outline"
                size="lg"
                w="100%"
                onClick={handleVoltar}
                borderRadius="xl"
                fontWeight="bold"
                fontSize="md"
                py={6}
                _hover={{ bg: "gray.50", transform: "translateY(-2px)", boxShadow: "md" }}
                transition="all 0.2s"
              >
                Voltar
              </Button>
            </VStack>
          </Box>
        </Box>
      </Box>
    );
  }

  const empresaConfig = getEmpresaConfig(motorista.empresa);

  return (
         <Box bg={bgColor} minH="100vh" py={4}>
       <Container maxW="full" px={6}>
                 {/* Header com navegação */}
         <Flex justify="space-between" align="center" mb={10}>
          <HStack spacing={4}>
            <Button
              leftIcon={<FaArrowLeft />}
              colorScheme="gray"
              variant="ghost"
              size="lg"
              onClick={handleVoltar}
              _hover={{ bg: "gray.100", transform: "translateX(-2px)" }}
              borderRadius="xl"
              px={6}
              transition="all 0.2s"
              fontWeight="medium"
            >
              Voltar
            </Button>
            <Button
              leftIcon={<FaUsers />}
              colorScheme="gray"
              variant="ghost"
              size="lg"
              onClick={handleVoltarSelecao}
              _hover={{ bg: "gray.100", transform: "translateX(-2px)" }}
              borderRadius="xl"
              px={6}
              transition="all 0.2s"
              fontWeight="medium"
            >
              Selecionar Outro Motorista
            </Button>
          </HStack>
        </Flex>

        {/* Título e informações do motorista */}
                 <Card 
           bg={cardBg} 
           border="2px solid" 
           borderColor={empresaConfig.borderColor}
           borderRadius="2xl" 
           boxShadow="xl"
           mb={10}
           overflow="hidden"
         >
          <CardHeader 
            bg={empresaConfig.gradient}
            borderBottom="1px solid" 
            borderColor={borderColor}
          >
            <Flex align="center" gap={4}>
                             <Box
                 w="60px"
                 h="60px"
                 borderRadius="full"
                 border="2px solid"
                 borderColor={empresaConfig.borderColor}
                 bg="white"
                 overflow="hidden"
                 display="flex"
                 alignItems="center"
                 justifyContent="center"
                 boxShadow="lg"
                 flexShrink={0}
               >
                                   {empresaConfig.logo ? (
                    <Image
                      src={empresaConfig.logo}
                      alt={`Logo ${motorista.empresa}`}
                      w="100%"
                      h="100%"
                      objectFit="contain"
                      borderRadius="full"
                    />
                  ) : (
                    <Icon 
                      as={empresaConfig.icon} 
                      color={empresaConfig.borderColor} 
                      boxSize={8} 
                    />
                  )}
               </Box>
              <Box flex={1}>
                <Heading size="lg" color={textColor} mb={2}>
                 {motorista.nome}
                </Heading>
                <HStack spacing={4} flexWrap="wrap">
                  <Badge
                    colorScheme={empresaConfig.color}
                    variant="solid"
                    fontSize="sm"
                    px={3}
                    py={1}
                    borderRadius="full"
                    fontWeight="medium"
                  >
                    {motorista.empresa}
                  </Badge>
                  <Badge
                    colorScheme="gray"
                    variant="outline"
                    fontSize="sm"
                    px={3}
                    py={1}
                    borderRadius="full"
                    fontWeight="medium"
                  >
                    Código: {motorista.codigo}
                  </Badge>
                  <Badge
                    colorScheme="blue"
                    variant="outline"
                    fontSize="sm"
                    px={3}
                    py={1}
                    borderRadius="full"
                    fontWeight="medium"
                  >
                    {eventos.length} Eventos
                  </Badge>
                </HStack>
              </Box>
            </Flex>
          </CardHeader>
        </Card>

                 {/* Estatísticas */}
         <Box display="flex" justifyContent="center" mb={10}>
           <SimpleGrid columns={{ base: 1, md: 2, lg: 6 }} gap={6} w="full" maxW="full">
                     <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="xl" boxShadow="lg" w="full">
             <CardBody textAlign="center" p={6}>
               <VStack spacing={3}>
                 <Icon as={FaChartBar} color="blue.500" boxSize={8} />
                 <Stat>
                   <StatLabel color={mutedTextColor} fontSize="xs" fontWeight="medium">Total</StatLabel>
                   <StatNumber color={textColor} fontSize="2xl" fontWeight="bold">{stats.total}</StatNumber>
                 </Stat>
               </VStack>
             </CardBody>
           </Card>
          
                     <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="xl" boxShadow="lg" w="full">
             <CardBody textAlign="center" p={6}>
               <VStack spacing={3}>
                 <Icon as={FaDirections} color="green.500" boxSize={8} />
                 <Stat>
                   <StatLabel color="green.500" fontSize="xs" fontWeight="medium">Direção</StatLabel>
                   <StatNumber color="green.500" fontSize="2xl" fontWeight="bold">{stats.direcao}</StatNumber>
                 </Stat>
               </VStack>
             </CardBody>
           </Card>
          
          <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="xl" boxShadow="lg" w="full">
            <CardBody textAlign="center" p={6}>
              <VStack spacing={3}>
                <Icon as={FaUtensils} color="orange.500" boxSize={8} />
                <Stat>
                  <StatLabel color="orange.500" fontSize="xs" fontWeight="medium">Refeição</StatLabel>
                  <StatNumber color="orange.500" fontSize="2xl" fontWeight="bold">{stats.refeicao}</StatNumber>
                </Stat>
              </VStack>
            </CardBody>
          </Card>
          
          <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="xl" boxShadow="lg" w="full">
            <CardBody textAlign="center" p={6}>
              <VStack spacing={3}>
                <Icon as={FaPause} color="blue.500" boxSize={8} />
                <Stat>
                  <StatLabel color="blue.500" fontSize="xs" fontWeight="medium">Interstício</StatLabel>
                  <StatNumber color="blue.500" fontSize="2xl" fontWeight="bold">{stats.intersticio}</StatNumber>
                </Stat>
              </VStack>
            </CardBody>
          </Card>
          
          <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="xl" boxShadow="lg" w="full">
            <CardBody textAlign="center" p={6}>
              <VStack spacing={3}>
                <Icon as={FaHourglassHalf} color="yellow.500" boxSize={8} />
                <Stat>
                  <StatLabel color="yellow.500" fontSize="xs" fontWeight="medium">Espera</StatLabel>
                  <StatNumber color="yellow.500" fontSize="2xl" fontWeight="bold">{stats.tempoEspera}</StatNumber>
                </Stat>
              </VStack>
            </CardBody>
          </Card>
          
          <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="xl" boxShadow="lg" w="full">
            <CardBody textAlign="center" p={6}>
              <VStack spacing={3}>
                <Icon as={FaClock} color="green.500" boxSize={8} />
                <Stat>
                  <StatLabel color="green.500" fontSize="xs" fontWeight="medium">Ativos</StatLabel>
                  <StatNumber color="green.500" fontSize="2xl" fontWeight="bold">{stats.ativos}</StatNumber>
                </Stat>
              </VStack>
            </CardBody>
          </Card>
        
        </SimpleGrid>
        </Box>

                                   {/* Filtros */}
          <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="xl" boxShadow="lg" mb={10}>
           <CardBody p={8}>
             <VStack spacing={8} align="stretch">
               {/* Filtros de Status e Tipo */}

               <Box>
                 <Text fontSize="md" fontWeight="bold" color={textColor} mb={3}>
                   Período de Datas:
                 </Text>
                 <HStack spacing={6} flexWrap="wrap">
                   <Button
                     size="md"
                     colorScheme={periodoSelecionado === "ontem" ? "blue" : "gray"}
                     variant={periodoSelecionado === "ontem" ? "solid" : "outline"}
                     onClick={() => alterarPeriodo("ontem")}
                     borderRadius="lg"
                     px={6}
                     py={3}
                   >
                     Ontem
                   </Button>
                   <Button
                     size="md"
                     colorScheme={periodoSelecionado === "hoje" ? "blue" : "gray"}
                     variant={periodoSelecionado === "hoje" ? "solid" : "outline"}
                     onClick={() => alterarPeriodo("hoje")}
                     borderRadius="lg"
                     px={6}
                     py={3}
                   >
                     Hoje
                   </Button>
                   <Button
                     size="md"
                     colorScheme={periodoSelecionado === "3dias" ? "blue" : "gray"}
                     variant={periodoSelecionado === "3dias" ? "solid" : "outline"}
                     onClick={() => alterarPeriodo("3dias")}
                     borderRadius="lg"
                     px={6}
                     py={3}
                   >
                     3 Dias
                   </Button>
                   <Button
                     size="md"
                     colorScheme={periodoSelecionado === "semana" ? "blue" : "gray"}
                     variant={periodoSelecionado === "semana" ? "solid" : "outline"}
                     onClick={() => alterarPeriodo("semana")}
                     borderRadius="lg"
                     px={6}
                     py={3}
                   >
                     Semana
                   </Button>
                   <Button
                     size="md"
                     colorScheme={periodoSelecionado === "mes" ? "blue" : "gray"}
                     variant={periodoSelecionado === "mes" ? "solid" : "outline"}
                     onClick={() => alterarPeriodo("mes")}
                     borderRadius="lg"
                     px={6}
                     py={3}
                   >
                     Mês
                   </Button>
                 </HStack>
                                   <HStack spacing={6} mt={4} flexWrap="wrap">
                    <Text fontSize="sm" color={mutedTextColor}>
                      Período atual: {dataInicial} até {dataFinal}
                    </Text>
                    <Button
                      size="sm"
                      colorScheme="blue"
                      variant="outline"
                      onClick={() => carregarEnderecos(eventos)}
                      isLoading={carregandoEnderecos}
                      leftIcon={<FaMapMarkerAlt />}
                      borderRadius="lg"
                      px={4}
                      py={2}
                    >
                      Recarregar Endereços
                    </Button>
                  </HStack>
               </Box>
             </VStack>
           </CardBody>
         </Card>

                 {/* Lista de eventos */}
         {filteredEventos.length === 0 ? (
           <Card 
             bg={cardBg} 
             border="1px solid" 
             borderColor="yellow.200" 
             borderRadius="2xl" 
             boxShadow="xl"
             overflow="hidden"
           >
             <CardHeader bg="yellow.500" textAlign="center">
               <Text fontSize="2xl" fontWeight="bold" color="white" mb={2}>
                 Nenhum Evento Encontrado
               </Text>
               <Text fontSize="md" color="yellow.100" opacity={0.9}>
                 {eventos.length === 0 
                   ? "Este motorista não possui eventos registrados"
                   : "Nenhum evento corresponde aos filtros aplicados"
                 }
               </Text>
             </CardHeader>

             <CardBody p={8} textAlign="center">
               <Text fontSize="lg" color={textColor} mb={6}>
                 {eventos.length === 0 
                   ? "Não há eventos registrados para este motorista no momento."
                   : "Tente ajustar os filtros ou limpe-os para ver todos os eventos."
                 }
               </Text>
               
               {eventos.length === 0 && (
                 <Button
                   colorScheme="blue"
                   size="lg"
                   onClick={() => loadEventos(dataInicial, dataFinal)}
                   borderRadius="xl"
                   fontWeight="bold"
                   fontSize="md"
                   py={6}
                   px={8}
                   _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
                   transition="all 0.2s"
                 >
                   Atualizar Eventos
                 </Button>
               )}
             </CardBody>
           </Card>
         ) : (
                       <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="xl" boxShadow="lg" mb={10}>
             <CardHeader>
               <Text fontSize="lg" fontWeight="bold" color={textColor}>
                 Lista de Eventos ({filteredEventos.length} eventos)
               </Text>
             </CardHeader>
             <CardBody p={0}>
               <TableContainer>
                 <Table variant="simple" size="sm">
                   <Thead>
                     <Tr>
                       <Th>Evento</Th>
                       <Th>Data</Th>
                       <Th>Horário</Th>
                       <Th>Duração</Th>
                       <Th>Placa</Th>
                       <Th>Status</Th>
                       <Th>Observação</Th>
                                               <Th>
                          <HStack spacing={2}>
                            <Text>Localização</Text>
                            {carregandoEnderecos && (
                              <Spinner size="xs" color="blue.500" />
                            )}
                          </HStack>
                        </Th>
                     </Tr>
                   </Thead>
                   <Tbody>
                     {filteredEventos.map((evento) => {
                       const eventoConfig = getEventoConfig(evento.descricao_evento);
                       const statusConfig = getStatusConfig(evento.status);
                       
                       return (
                         <Tr key={evento.id} _hover={{ bg: "gray.50" }}>
                           <Td>
                             <HStack spacing={2}>
                               <Icon as={eventoConfig.icon} color={eventoConfig.borderColor} boxSize={4} />
                               <Text fontWeight="medium">{eventoConfig.label}</Text>
                               {isEventoEmAndamento(evento.data_inicial, evento.hora_inicial, evento.data_final, evento.hora_final, evento.descricao_evento) && (
                                 <Badge colorScheme="green" variant="solid" size="sm">
                                   ATIVO
                                 </Badge>
                               )}
                             </HStack>
                           </Td>
                                                       <Td>
                              {(() => {
                                try {
                                  // Formatação manual da data para evitar problemas de timezone
                                  const [ano, mes, dia] = evento.data_inicial.split('-');
                                  
                                  // Formatar manualmente como DD/MM/YYYY
                                  const diaFormatado = dia.padStart(2, '0');
                                  const mesFormatado = mes.padStart(2, '0');
                                  const resultado = `${diaFormatado}/${mesFormatado}/${ano}`;
                                  
                                  return resultado;
                                } catch (error) {
                                  console.error("Erro ao formatar data:", error);
                                  return evento.data_inicial;
                                }
                              })()}
                            </Td>
                           <Td>
                             <VStack spacing={0} align="start">
                               <Text fontSize="xs" color="green.500">Início: {evento.hora_inicial}</Text>
                               {!isEventoEmAndamento(evento.data_inicial, evento.hora_inicial, evento.data_final, evento.hora_final, evento.descricao_evento) && (
                                 <Text fontSize="xs" color="red.500">Fim: {evento.hora_final}</Text>
                               )}
                               {isEventoEmAndamento(evento.data_inicial, evento.hora_inicial, evento.data_final, evento.hora_final, evento.descricao_evento) && (
                                 <Text fontSize="xs" color="blue.500" fontWeight="medium">🔄 Em andamento...</Text>
                               )}
                             </VStack>
                           </Td>
                           <Td>
                             <VStack spacing={1} align="start">
                               <HStack spacing={2}>
                                 <Badge 
                                   colorScheme={isEventoEmAndamento(evento.data_inicial, evento.hora_inicial, evento.data_final, evento.hora_final, evento.descricao_evento) ? "green" : "blue"} 
                                   variant="outline"
                                 >
                                   {calculateDuration(evento.data_inicial, evento.hora_inicial, evento.data_final, evento.hora_final, evento.descricao_evento)}
                                 </Badge>
                                 {isEventoEmAndamento(evento.data_inicial, evento.hora_inicial, evento.data_final, evento.hora_final, evento.descricao_evento) && (
                                   <Icon as={FaClock} color="green.500" boxSize={3} />
                                 )}
                               </HStack>
                               {isEventoEmAndamento(evento.data_inicial, evento.hora_inicial, evento.data_final, evento.hora_final, evento.descricao_evento) && (
                                 <Text fontSize="xs" color="green.600" fontWeight="medium">
                                   ⏱️ Atualizado em tempo real
                                 </Text>
                               )}
                             </VStack>
                           </Td>
                           <Td>
                             <Badge colorScheme="gray" variant="solid">
                               {evento.placa}
                             </Badge>
                           </Td>
                           <Td>
                             {evento.status ? (
                               <Badge colorScheme={statusConfig.color} variant="solid">
                                 {statusConfig.label}
                               </Badge>
                             ) : (
                               <Text fontSize="xs" color="gray.400">-</Text>
                             )}
                           </Td>
                           <Td minW="250px">
                             {evento.observacao ? (
                               <Text fontSize="sm" noOfLines={2} maxW="150px">
                                 {evento.observacao}
                               </Text>
                             ) : (
                               <Text fontSize="xs" color="gray.400">-</Text>
                             )}
                           </Td>
                                                       <Td> 
                              <VStack spacing={0} align="start">
                                {carregandoEnderecos ? (
                                  <HStack spacing={2}>
                                    <Spinner size="xs" color="blue.500" />
                                    <Text fontSize="xs" color="gray.500">Carregando endereço...</Text>
                                  </HStack>
                                ) : (
                                  <Text fontSize="sm" color="blue.600" fontWeight="medium">
                                    {enderecos[evento.id] || "Endereço não disponível"}
                                  </Text>
                                )}
                                <Text fontSize="xs" color="gray.500">
                                  Coord: {evento.latitude?.toFixed(4)}, {evento.longitude?.toFixed(4)}
                                </Text>
                              </VStack>
                            </Td>
                         </Tr>
                       );
                     })}
                   </Tbody>
                 </Table>
               </TableContainer>
             </CardBody>
           </Card>
         )}
      </Container>

      {/* SSL Certificate Helper Modal */}
      <SSLCertificateHelper
        isOpen={sslHelperDisclosure.isOpen}
        onClose={sslHelperDisclosure.onClose}
        error={sslError}
        baseUrl={sslError?.baseUrl}
      />
    </Box>
  );
}

export default EventosAtuais;
